/**
 * Public Booking SPA — entry point.
 *
 * Initializes styles, registers routes, and starts the hash router.
 */
import { addRoute, initRouter, state, navigate } from './router.js';
import { renderServiceList } from './pages/service-list.js';
import { renderTenantHeader } from './pages/tenant-header.js';
import { renderSlotPicker } from './pages/slot-picker.js';
import { renderBookingForm } from './pages/booking-form.js';
import { renderConfirmation } from './pages/confirmation.js';
import { renderPaymentReturn } from './pages/payment-return.js';
import { renderStatusCheck } from './pages/status-check.js';
import { renderReschedulePage } from './pages/reschedule.js';
import { renderWaitlistJoin } from './pages/waitlist-join.js';
import { renderBookingError } from './pages/booking-error.js';
import { initCookieConsent } from '../../../shared/frontend/js/cookie-consent.js';
import { initPublicI18n } from './i18n/i18nPublic.js';

// Import styles
import './styles/public-reset.css';
import './styles/public-tokens.css';
import './styles/public-components.css';
import './styles/public-utilities.css';
import './styles/public-layout.css';
import './styles/public-e7.css';

// ── Route registration ─────────────────────────────────────────────────

// #/services — List available services
addRoute(/^\/services$/, 'services', async () => {
  await renderServiceList();
});

// #/book/:serviceId — Pick a date and time slot
addRoute(/^\/book\/(?<serviceId>[a-f0-9-]+)$/, 'book', async (params) => {
  const serviceId = params['serviceId'];
  if (serviceId) {
    state.selectedServiceId = serviceId;
    await renderSlotPicker(serviceId);
  }
});

// #/confirm — Enter customer details and submit booking
addRoute(/^\/confirm$/, 'confirm', () => {
  if (!state.selectedServiceId || !state.selectedSlot) {
    navigate('/services');
    return;
  }
  renderBookingForm();
});

// #/confirmation — Show booking confirmation
addRoute(/^\/confirmation$/, 'confirmation', () => {
  if (!state.lastBooking) {
    navigate('/services');
    return;
  }
  renderConfirmation();
});

// #/payment-return?pi=… — Return from a Stripe redirect payment (Revolut Pay / Klarna /
// redirecting 3DS). The page reads `pi` from the hash + the stashed client_secret.
addRoute(/^\/payment-return(?:\?.*)?$/, 'payment-return', async () => {
  await renderPaymentReturn();
});

// #/reschedule/:bookingId — Self-service reschedule
addRoute(/^\/reschedule\/(?<bookingId>[a-f0-9-]+)$/, 'reschedule', async (params) => {
  const bookingId = params['bookingId'];
  if (bookingId) {
    // Extract token from query string in hash
    const hashParts = window.location.hash.split('?');
    const searchParams = new URLSearchParams(hashParts[1] ?? '');
    const token = searchParams.get('token') ?? '';
    await renderReschedulePage({ bookingId, token });
  }
});

// #/status — Check booking status
addRoute(/^\/status$/, 'status', () => {
  renderStatusCheck();
});

// #/waitlist?service=:id — Join the waitlist (epic7). The page reads its own
// query params (service/serviceName/resource) from the hash, so the route only
// needs to match the path + optional trailing query string.
addRoute(/^\/waitlist(?:\?.*)?$/, 'waitlist', () => {
  renderWaitlistJoin();
});

// #/error?code=:code — Branded booking-error card (epic7). The page reads its
// own query params (code/service/detail/…) from the hash.
addRoute(/^\/error(?:\?.*)?$/, 'error', () => {
  renderBookingError();
});

// #/portal/:token — Customer portal (secure token link)
addRoute(/^\/portal\/(?<token>[a-f0-9]{96})$/, 'portal', () => {
  renderStatusCheck(); // renderStatusCheck detects portal token in hash
});

// ── Stripe redirect-return handoff ──────────────────────────────────────
// A redirect payment returns to `/b/:slug/booking-return?payment_intent=…&
// payment_intent_client_secret=…&redirect_status=…` (a real server route serving this
// SPA shell). Stripe appends those params to the query — the hash router can't see them
// and a full-page redirect loses in-memory state. So before the router starts we: parse
// the params, persist them in a RELOAD-SURVIVING stash (history.state + sessionStorage),
// strip the query (keep the client_secret out of the address bar/history/logs), and set
// the hash to #/payment-return?pi=… so the router renders the return page.
function handlePaymentReturnBootstrap(): void {
  if (!window.location.pathname.endsWith('/booking-return')) return;
  const qs = new URLSearchParams(window.location.search);
  const paymentIntent = qs.get('payment_intent');
  const clientSecret = qs.get('payment_intent_client_secret');
  if (!paymentIntent || !clientSecret) return;

  const stash = {
    payment_intent: paymentIntent,
    client_secret: clientSecret,
    redirect_status: qs.get('redirect_status') ?? '',
    created_at: Date.now(),
  };
  try {
    window.sessionStorage.setItem(`ts_pay_return_${paymentIntent}`, JSON.stringify(stash));
  } catch {
    /* sessionStorage unavailable — history.state below still carries it */
  }
  const cleanUrl = `${window.location.pathname}#/payment-return?pi=${encodeURIComponent(paymentIntent)}`;
  window.history.replaceState({ timeslotPaymentReturn: stash }, '', cleanUrl);
}

// ── Initialize ─────────────────────────────────────────────────────────

/**
 * Mount the branded tenant header ONCE into the persistent `#ts-tenant-header`
 * container that lives in the SSR shell ABOVE `<main id="app">`. Because it sits
 * outside `#app`, the hash router's `appEl.innerHTML = …` churn on every step
 * (slot-picker → details → confirm/checkout) never wipes it, so the company
 * banner (logo + business name + welcome message + today's hours) stays visible
 * on EVERY booking step — not just the initial service list.
 *
 * Fire-and-forget: a failed/aborted branding fetch degrades to a minimal header
 * inside renderTenantHeader, so the booking flow always renders regardless.
 */
function mountPersistentTenantHeader(): void {
  const slot = document.getElementById('ts-tenant-header');
  if (slot) {
    void renderTenantHeader(slot);
  }
}

/**
 * CLIENT-SIDE demo "Customer view" exit-bar.
 *
 * The `/b/:slug` SSR HTML is nginx-cached (Cache-Control: public, max-age=300),
 * so a single cached copy is served to EVERY viewer regardless of their
 * `ts_demo` cookie. Rendering the bar server-side (cookie-gated) was therefore
 * broken: whichever request warmed the cache decided whether ALL viewers saw
 * the bar. So the SSR HTML is now identical for everyone (cache-safe, SEO-safe —
 * no bar element, only its CSS), and we inject the bar here from the viewer's
 * actual `document.cookie`.
 *
 * The bar is inserted as the FIRST child of <body> — above `#ts-tenant-header`
 * and `#app` — so it sits outside the hash router's `#app` churn and persists
 * across every booking step. Idempotent: a guard prevents a double-insert.
 */
export function injectDemoExitBar(): void {
  // Demo sessions carry a readable `ts_demo=1` cookie (set by POST /v1/demo/allocate).
  if (!/(?:^|;\s*)ts_demo=1(?:;|$)/.test(document.cookie)) return;
  // Idempotency guard — never insert twice.
  if (document.querySelector('.ts-demobar')) return;

  const config = (window as unknown as { __TIMESLOT_CONFIG__?: { panelBasePath?: string } })
    .__TIMESLOT_CONFIG__;
  const panelBasePath =
    typeof config?.panelBasePath === 'string' && config.panelBasePath
      ? config.panelBasePath
      : '/panel-9e5b309e/';

  const bar = document.createElement('div');
  bar.className = 'ts-demobar';
  bar.setAttribute('role', 'region');
  bar.setAttribute('aria-label', 'Demo customer view');

  const label = document.createElement('span');
  label.className = 'ts-demobar__label';
  const eye = document.createElement('span');
  eye.setAttribute('aria-hidden', 'true');
  eye.textContent = '\u{1F441}️';
  const strong = document.createElement('strong');
  strong.textContent = 'Customer view';
  label.append(
    eye,
    document.createTextNode(' '),
    strong,
    document.createTextNode(' — this is what your customers see when they open your booking link.'),
  );

  const actions = document.createElement('span');
  actions.className = 'ts-demobar__actions';
  const back = document.createElement('a');
  back.className = 'ts-demobar__btn';
  back.href = panelBasePath;
  back.textContent = '← Back to dashboard';
  const exit = document.createElement('a');
  exit.className = 'ts-demobar__btn ts-demobar__btn--ghost';
  exit.href = '/demo';
  exit.textContent = 'Exit demo';
  actions.append(back, exit);

  bar.append(label, actions);
  // Insert as the very first child of <body> (above #ts-tenant-header and #app).
  document.body.insertBefore(bar, document.body.firstChild);
}

async function bootstrap(): Promise<void> {
  handlePaymentReturnBootstrap();
  // Resolve locale, set <html lang>, and load public translations BEFORE the
  // first route renders so tPublic() resolves real copy on initial paint.
  // initPublicI18n never throws (bundled fallback + best-effort API overlay).
  await initPublicI18n();
  // Demo "Customer view" bar — injected per the viewer's own cookie (cache-safe).
  injectDemoExitBar();
  // Persistent company header — rendered once, survives all SPA step navigations.
  mountPersistentTenantHeader();
  initRouter();
  initCookieConsent();
}

document.addEventListener('DOMContentLoaded', () => {
  void bootstrap();
});

// If DOM is already loaded (embed scenario)
if (document.readyState !== 'loading') {
  void bootstrap();
}
